import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import {
  map,
  pipe,
  find,
  propEq,
  prop,
  values as rValues,
  flatten,
  sortBy,
  reverse,
  filter,
  pathOr,
  complement,
  isEmpty
} from 'ramda'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { noop } from 'lodash'
import { Row, FieldDropdown, Group } from '@/components/form'
import { Button } from '@/components/button'
import { select } from '@/store'
import { createModal, useModalize } from '@/components/modalize/mui'
import useMediaQuery from '@/hooks/use-media-query'
import { fontWeights, breakpoints } from '@/styles'
import ModalBase from '@/components/modal/modal-base'
import Context from '@/components/modal/context'
import { getFormattedSigners, getFormattedPackageFiles } from '../utils'
import getUniqueColor from '../get-unique-color'

const Wrapper = styled.div`
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;
  width: 100%;
  > :first-child {
    margin-bottom: 24px;
  }
`

const ModalText = styled.p`
  font-weight: ${fontWeights.regular};
  font-size: 14px;
  line-height: 16px;
  margin: 0 0 24px 0;
`

const FormWrapper = styled(Form)`
  width: 100%;
`

const SelectApplicantModal = ({
  contractId,
  ModalTitle,
  setSignees = noop,
  fileMetadata,
  setFileMetadata = noop
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const {
    contract = {},
    applicants = {},
    recipients = {},
    packageFiles = []
  } = useSelector(state => select.contract.contractPackageInfo(state, { contractId }))

  const listingId = contract.listingId
  const sentDocuments = pipe(filter(propEq('state_machine', 'sent')))(packageFiles)
  const hasSentDocument = pipe(complement(isEmpty))(sentDocuments)

  const formattedApplicants = pipe(
    Object.entries,
    map(([applicantListingId, value]) => {
      return {
        options: value?.applicants?.map(x => ({
          value: x.application_id,
          label: `${x.first_name} ${x.last_name}`
        })),
        label:
          applicantListingId === listingId
            ? t('l.contract.create.current_listing.title', 'Current Listing')
            : value?.address
      }
    }),
    sortBy(propEq('label', t('l.contract.create.current_listing.title', 'Current Listing'))),
    reverse
  )(applicants)

  const getContractdocuments = () => {
    const applicantColor = getUniqueColor(recipients, packageFiles)

    const contractDocuments = packageFiles.map(doc => {
      return {
        id: doc.id,
        listing_contract_package_file_id: doc.id,
        recipient_signing_status: hasSentDocument
          ? 'email_notification_required'
          : 'signature_required',
        metadata: {
          color: applicantColor
        }
      }
    })

    return contractDocuments
  }

  const handleOnSubmit = async values => {
    const applicant = pipe(
      rValues,
      map(prop('applicants')),
      flatten,
      find(propEq('application_id', values.applicant))
    )(applicants)

    const signer = {
      application_id: applicant.application_id,
      user_id: applicant.user_id,
      contract_documents: getContractdocuments()
    }

    await dispatch.contract.addRecipient({
      signer: signer,
      contractId
    })

    const removedApplicants = pipe(
      pathOr([], [applicant.listing_id, 'applicants']),
      filter(complement(propEq('application_id', applicant.application_id)))
    )(applicants)

    dispatch.contract.updateAt(
      [contractId, 'applicants', applicant.listing_id, 'applicants'],
      removedApplicants
    )

    const response = await dispatch.contract.getPackage({ contractId: contractId })
    const formattedSigners = getFormattedSigners(response?.body?.signers)
    const formattedFiles = getFormattedPackageFiles(response?.body?.package_files)

    setSignees(formattedSigners)
    setFileMetadata({
      ...fileMetadata,
      package_files: formattedFiles,
      signer: response?.body?.signers
    })

    close()
  }

  const initialValues = {
    applicant: ''
  }

  const validationSchema = Yup.object().shape({
    applicant: Yup.string().required()
  })

  return (
    <Context.Provider
      value={{
        ModalTitle,
        close
      }}
    >
      <ModalBase
        overflow="visible"
        minHeight={295}
        title={t('l.contract.create.select_an_applicant.title', 'Select an applicant')}
      >
        <Wrapper>
          <ModalText>
            {t(
              'l.contract.create.select_an_applicant.text',
              'Applicants who have applied to other listings within the past two months may also be selected.'
            )}
          </ModalText>
          <Formik
            initialValues={initialValues}
            onSubmit={handleOnSubmit}
            validationSchema={validationSchema}
          >
            {({ isSubmitting }) => {
              return (
                <FormWrapper>
                  <Row>
                    <Group>
                      <FieldDropdown
                        name="applicant"
                        options={formattedApplicants}
                        placeholder={t('l.contract.create.applicant.title', 'Applicant')}
                        isSearchable
                      />
                    </Group>
                  </Row>
                  <Button
                    type="submit"
                    theme="primary"
                    disabled={isSubmitting}
                    size={displayMobile ? 'medium' : 'small'}
                    width={'100%'}
                  >
                    {t('l.contract.create.continue.text', 'Continue')}
                  </Button>
                </FormWrapper>
              )
            }}
          </Formik>
        </Wrapper>
      </ModalBase>
    </Context.Provider>
  )
}

export const show = createModal(SelectApplicantModal, {
  id: 'select-applicant-modal',
  width: '640px',
  sheet: true,
  overflow: 'visible'
})
